import React, { useState, useRef } from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const HtmlToPdf = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [htmlText, setHtmlText] = useState('');
  const [submitValue, setSubmitValue] = useState('Convert to PDF');
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState(false);
  const [notify, setNotify] = useState('');
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const toggleModal = () => {
    setVisible(!visible);
    resetState();
  };

  const resetState = () => {
    setFile(null);
    setHtmlText('');
    setSubmitValue('Convert to PDF');
    setWorking(false);
    setDone(false);
    setNotify('');
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const isHtml =
      /\.html?$/i.test(selectedFile.name) || selectedFile.type === 'text/html';
    if (!isHtml) {
      setNotify('Only .html or .htm files are allowed');
      setError('error');
      return;
    }

    setNotify('');
    setError(null);
    setDone(false);
    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (e) => {
      setHtmlText(e.target.result || '');
    };
    reader.onerror = () => {
      setNotify('Failed to read the file. Please try again.');
      setError('error');
    };
    reader.readAsText(selectedFile);
  };

  // Strip anything that could execute JS against the app, then return the body
  // markup. We parse the uploaded document in an inert DOMParser tree (which
  // never runs scripts) and remove all <script> elements plus inline event
  // handler attributes / javascript: URLs before the markup ever touches the
  // live DOM via innerHTML.
  const sanitizeHtml = (rawHtml) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml || '', 'text/html');

    // Remove script tags entirely.
    doc.querySelectorAll('script').forEach((node) => node.remove());

    // Remove inline event handlers and javascript: hrefs/srcs.
    doc.querySelectorAll('*').forEach((el) => {
      [...el.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();
        const val = (attr.value || '').trim().toLowerCase();
        if (name.startsWith('on')) {
          el.removeAttribute(attr.name);
        } else if (
          (name === 'href' || name === 'src' || name === 'xlink:href') &&
          val.startsWith('javascript:')
        ) {
          el.removeAttribute(attr.name);
        }
      });
    });

    // Prefer the parsed <body> content; fall back to the raw string with a
    // belt-and-suspenders regex strip if parsing produced nothing.
    const bodyHtml = doc.body ? doc.body.innerHTML : '';
    if (bodyHtml && bodyHtml.trim()) return bodyHtml;
    return (rawHtml || '').replace(/<script[\s\S]*?<\/script>/gi, '');
  };

  const convertToPdf = async () => {
    if (!file || !htmlText) return;

    setWorking(true);
    setSubmitValue(<Spinner />);
    setNotify('');
    setError(null);

    // Offscreen, isolated render container (~A4 width @ 96dpi). Scripts are
    // already stripped from the markup before it is assigned here.
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '794px';
    container.style.background = '#fff';
    container.style.padding = '24px';
    container.style.boxSizing = 'border-box';
    container.style.color = '#000';
    container.style.fontFamily = 'Arial, Helvetica, sans-serif';
    container.style.lineHeight = '1.5';

    try {
      container.innerHTML = sanitizeHtml(htmlText);
      // Keep images from overflowing the A4 width.
      container.querySelectorAll('img').forEach((img) => {
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
      });
      document.body.appendChild(container);

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: container.scrollWidth,
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });

      // A4 in mm. Map the full-width canvas onto the page width and slice the
      // tall image across as many A4 pages as needed (mirror of docx-to-pdf
      // pagination math).
      const pageWidthMm = 210;
      const pageHeightMm = 297;
      const imgWidthMm = pageWidthMm;
      const imgHeightMm = (canvas.height * imgWidthMm) / canvas.width;

      const imgData = canvas.toDataURL('image/jpeg', 0.95);

      let heightLeft = imgHeightMm;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidthMm, imgHeightMm);
      heightLeft -= pageHeightMm;

      while (heightLeft > 0) {
        position -= pageHeightMm;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidthMm, imgHeightMm);
        heightLeft -= pageHeightMm;
      }

      const blob = pdf.output('blob');
      const baseName = file.name.replace(/\.[^/.]+$/, '') || 'document';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addUsage('AWESOME PDF TOOLS HtmlToPdf', '1.0', 1, window.location.href);

      setSubmitValue('Success');
      setDone(true);
    } catch (err) {
      console.error('Error converting HTML to PDF:', err);
      setNotify('Failed to convert HTML to PDF. Please try again.');
      setError('error');
      setSubmitValue('Convert to PDF');
    } finally {
      setWorking(false);
      if (container && document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }
  };

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <svg viewBox="0 0 64 64" fill="none" style={{ width: 42, height: 42 }}>
            {/* Document sheet */}
            <path
              d="M14 6h26l12 12v40a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z"
              fill="#FF9900"
            />
            {/* Folded corner */}
            <path d="M40 6l12 12H42a2 2 0 0 1-2-2V6z" fill="#CC7A00" />
            {/* HTML angle brackets */}
            <path
              d="M24 30l-6 6 6 6"
              stroke="#FFFFFF"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M40 30l6 6-6 6"
              stroke="#FFFFFF"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M34 28l-4 16" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </div>
        <h3 className="service-card-title">Convert HTML to PDF</h3>
        <p className="service-card-desc">Turn an HTML file into a clean, shareable PDF</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              <Button variant="primary" disabled={!file || working || error} onClick={convertToPdf}>
                {submitValue}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="Convert HTML to PDF"
      >
        <div>
          <div className="bordered-box">
            <h6 className="container-header">🌐 Choose HTML File</h6>
            <FormField
              label="Drag & drop your .html file here, or click to browse"
              description="External images, fonts and stylesheets may not be captured. For security, any scripts in the file are removed before rendering."
              errorText={notify}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".html,.htm,text/html"
                onChange={handleFileChange}
              />
            </FormField>
            {file && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                Selected: {file.name}
              </div>
            )}
          </div>

          {done && (
            <div className="bordered-box">
              <h6 className="container-header">✅ PDF Ready!</h6>
              <div
                style={{
                  backgroundColor: '#e6f7e6',
                  border: '1px solid #52c41a',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                }}
              >
                <span style={{ color: '#135200' }}>
                  📄 Your PDF was generated and downloaded • 🔒 100% Private • 🆓 Free
                </span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Button variant="link" onClick={resetState}>
                  Convert another
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default HtmlToPdf;
